// Centralized map state (Zustand) — replaces the former `window.__*` globals
// that bridged App ⇆ MapView. MapLibre event handlers registered once on first
// render need the *latest* value synchronously; reading `useMapStore.getState()`
// inside a handler is always fresh (no stale closure), which is exactly what the
// window globals were doing — just without leaking onto `window`.
//
// Slices below mirror the OnlineMap V2 store spec. During Phase 1 App remains the
// React source of truth and syncs the tool-bridge fields here; components read
// them imperatively in map handlers or via selectors where re-render is wanted.
import { create } from 'zustand'

export const useMapStore = create((set) => ({
  // ── root ────────────────────────────────────────────────────────────────
  ownedCountryId: null,

  // ── selection slice (Plan Mode paint bridge) ────────────────────────────
  planModeActive: false,
  selectedColor: '#FF5733',
  selectionMode: 'single', // 'single' | 'area'

  // ── construction slice (structures + infrastructure draw bridge) ────────
  constructionModeActive: false,
  infrastructureModeActive: false,
  // Housing carries density/style/quality/capacity instead of spec/level —
  // see utils/structures.js:constructStructure.
  constructionSelection: {
    type: '', specialisation: '', level: null,
    density_id: null, style_id: null, quality_id: null, capacity: 1000,
  },
  skipConstructionConfirm: false,
  // Announce builds in the country's secret channel instead of its public one.
  notifySecret: false,
  infrastructurePath: [],
  structureTypeData: [],
  housingOptions: null,
  infrastructureData: [],
  // Transient imperative flags shared between MapView event handlers (were
  // window.__structDragActive / window.__suppressNextMapClick).
  structDragActive: false,
  suppressNextMapClick: false,

  // ── tronçon slice (infra editor) ───────────────────────────────────────
  // The infra tool has two mouse modes and they are mutually exclusive:
  //   'select' — the mouse navigates the map; clicking a route selects it.
  //   'draw'   — the left button draws instead of panning.
  // A "tronçon" is one built infrastructure row (server id); the selection is
  // multi and locked to one type (see utils/troncons.js). `infraConnectFrom`
  // holds the node index waiting for the second click of a "connecter à une
  // autre node" gesture.
  infraMode: 'select',
  selectedTroncIds: [],
  infraConnectFrom: null,

  // ── militaryPlanning slice ──────────────────────────────────────────────
  milPlanToolActive: false,

  // ── ruler slice ─────────────────────────────────────────────────────────
  rulerModeActive: false,
  rangeModeActive: false,

  // ── interactive legend slice ────────────────────────────────────────────
  // When set, regions NOT matching { prop === value } are greyed out.
  legendIsolate: null,
  setLegendIsolate: (iso) => set({ legendIsolate: iso }),

  // ── multi-selection slice (Shift/Ctrl+click) ───────────────────────────
  selectedRegionIds: [],
  toggleSelectedRegion: (id) => set((s) => ({
    selectedRegionIds: s.selectedRegionIds.includes(id)
      ? s.selectedRegionIds.filter((x) => x !== id)
      : [...s.selectedRegionIds, id],
  })),
  clearSelectedRegions: () => set({ selectedRegionIds: [] }),

  // ── viewport slice ──────────────────────────────────────────────────────
  // Current camera, kept fresh by MapView (useViewport) for bookmarks + share.
  viewport: { center: [20, 20], zoom: 2, bearing: 0 },
  // A target the map should fly to (bookmarks / shared-link restore). MapView
  // consumes it and clears it. { center, zoom, bearing } or null.
  flyTo: null,

  // Generic patch action — App syncs its React tool-state here in one call,
  // and MapView flips the transient flags. Kept minimal on purpose.
  patch: (partial) => set(partial),
}))

// Imperative accessor for use inside MapLibre event callbacks (always fresh).
export const mapState = () => useMapStore.getState()
